import {
  Button,
  ButtonProps,
  HtmlCheckbox,
  Text,
  TextProps,
  Radio,
  RadioGroup,
  RadioGroupProps,
  Tag,
} from "@ttc3k/trekker";
import { createContext, useContext } from "react";
import { Box, Flex, styled } from "styled-system/jsx";

const CardContainer = styled(Flex, {
  base: {
    borderRadius: "100",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "border.light",
    outlineWidth: "1px",
    outlineStyle: "solid",
    outlineColor: "transparent",
    py: "250",
    px: "300",
    bg: "white",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "200",
    transitionProperty: "border-color, outline-color",
    transitionDuration: "150ms",
    transitionTimingFunction: "ease-in-out",
    "& [data-scope=radio-group][data-part=item-control]:not([data-state=checked])":
      {
        borderColor: "border.mid",
      },
  },
  variants: {
    visual: {
      default: {},
      selected: {
        borderColor: "border.dark",
        outlineColor: "border.dark",
      },
    },
  },
});

type SelectableRadioCardGroupContextValue = {
  value: string;
  onValueChange: (value: string) => void;
};

const SelectableRadioCardGroupContext =
  createContext<SelectableRadioCardGroupContextValue | null>(null);

type SelectableRadioCardGroupProps = Omit<
  RadioGroupProps,
  "value" | "onValueChange"
> & {
  value: string;
  onValueChange: (value: string) => void;
};

/**
 * Renders a single radio group around a list of `SelectableRadioCard`s so the
 * cards share one tab stop and support arrow-key navigation.
 */
export const SelectableRadioCardGroup = ({
  value,
  onValueChange,
  children,
  ...rest
}: SelectableRadioCardGroupProps) => {
  return (
    <SelectableRadioCardGroupContext.Provider value={{ value, onValueChange }}>
      <RadioGroup
        value={value}
        onValueChange={({ value: next }: { value: string | null }) => {
          if (next) onValueChange(next);
        }}
        {...rest}
      >
        {children}
      </RadioGroup>
    </SelectableRadioCardGroupContext.Provider>
  );
};

type SelectableRadioCardProps = {
  radioValue: string;
  title: string;
  /** Ignored inside a `SelectableRadioCardGroup`, which provides the value. */
  selectedValue?: string;
  /** Omit for display-only cards that cannot be changed by clicking. */
  onSelectRadio?: () => void;
  subtitleProps?: TextProps;
  buttonProps?: ButtonProps;
  selectionIndicator?: "radio" | "checkbox" | "none";
  /** Tag shown next to the title while the card is selected. */
  selectedLabel?: string;
  disabled?: boolean;
};

export const SelectableRadioCard = ({
  selectedValue,
  radioValue,
  onSelectRadio,
  title,
  buttonProps,
  subtitleProps,
  selectionIndicator = "radio",
  selectedLabel,
  disabled = false,
}: SelectableRadioCardProps) => {
  const group = useContext(SelectableRadioCardGroupContext);
  const isSelected = (group?.value ?? selectedValue) === radioValue;
  const isInteractive = !!(onSelectRadio || group);

  const handleSelect = () => {
    if (disabled || !isInteractive) return;
    if (group) group.onValueChange(radioValue);
    else onSelectRadio?.();
  };

  // The radio or checkbox input is the focus target when one is rendered as
  // part of a real group; otherwise the card itself needs to be focusable.
  const controlIsFocusTarget =
    (selectionIndicator === "radio" && !!group) ||
    selectionIndicator === "checkbox";
  const cardIsFocusTarget = isInteractive && !controlIsFocusTarget;

  return (
    <CardContainer
      visual={isSelected ? "selected" : "default"}
      cursor={!isInteractive ? "default" : disabled ? "not-allowed" : "pointer"}
      opacity={disabled ? 0.5 : 1}
      onClick={handleSelect}
      onKeyDown={
        cardIsFocusTarget
          ? e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSelect();
              }
            }
          : undefined
      }
      role={cardIsFocusTarget ? (group ? "radio" : "button") : undefined}
      aria-checked={cardIsFocusTarget && group ? isSelected : undefined}
      aria-pressed={cardIsFocusTarget && !group ? isSelected : undefined}
      tabIndex={cardIsFocusTarget ? (disabled ? -1 : 0) : undefined}
      aria-disabled={isInteractive ? disabled : undefined}
    >
      <Flex flexDirection={"column"} gap={"50"} minW={0}>
        <Flex alignItems={"center"} gap={"150"} minW={0}>
          <Text
            visual={"bodySemiBold"}
            color={"text.dark"}
            textTransform={"capitalize"}
            lineClamp={1}
          >
            {title}
          </Text>
          {isSelected && selectedLabel && (
            <Tag visual={"highlight"} mode={"light"} size={"sm"} flexShrink={0}>
              {selectedLabel}
            </Tag>
          )}
        </Flex>
        {subtitleProps && (
          <Text visual={"smallMedium"} color={"text.mid"} {...subtitleProps} />
        )}
      </Flex>

      <Flex
        gap={"200"}
        alignItems={"center"}
        justifyContent={"flex-end"}
        flexShrink={0}
      >
        {buttonProps && (
          <Box onClick={e => e.stopPropagation()}>
            <Button {...buttonProps} />
          </Box>
        )}
        {buttonProps && selectionIndicator !== "none" && (
          <Box
            h={"20px"}
            borderLeftWidth={"2px"}
            borderColor={"border.light"}
            borderRadius={"full"}
          />
        )}
        {selectionIndicator === "radio" && (
          <Box onClick={e => e.stopPropagation()} display={"flex"}>
            {group ? (
              <Radio value={radioValue} disabled={disabled} />
            ) : (
              <RadioGroup
                value={selectedValue ?? ""}
                onValueChange={handleSelect}
              >
                <Radio value={radioValue} disabled={disabled} />
              </RadioGroup>
            )}
          </Box>
        )}
        {selectionIndicator === "checkbox" && (
          <Box onClick={e => e.stopPropagation()} display={"flex"}>
            <HtmlCheckbox
              checked={isSelected}
              disabled={disabled}
              onChange={handleSelect}
              aria-label={title}
            />
          </Box>
        )}
      </Flex>
    </CardContainer>
  );
};
